// components/SignIn.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

import robotColor from "../images/codiii-icon.svg";
import robotWhite from "../images/codiii-icon_white.svg";
import mascot from "../images/codiii-mascot.svg";
import msLogo from "../images/microsoft-logo.png";

const PageWrap = styled.div`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%);
`;

const RobotPattern = styled.div`
  position: absolute; inset: 0; pointer-events: none; z-index: 1;

  .robot-icon { position: absolute; width: 80px; height: 80px; opacity: 0.1; animation: float 20s infinite linear; }
  .robot-icon:nth-child(odd){ animation-direction: reverse; opacity: 0.08; }
  .robot-icon:nth-child(1){ top:10%; left:5%; animation-delay:0s; }
  .robot-icon:nth-child(2){ top:20%; right:8%; animation-delay:-3s; }
  .robot-icon:nth-child(3){ top:40%; left:12%; animation-delay:-6s; }
  .robot-icon:nth-child(4){ top:60%; right:15%; animation-delay:-9s; }
  .robot-icon:nth-child(5){ top:75%; left:20%; animation-delay:-12s; }
  .robot-icon:nth-child(6){ top:30%; left:50%; animation-delay:-15s; }
  .robot-icon:nth-child(7){ top:80%; right:30%; animation-delay:-18s; }
  .robot-icon:nth-child(8){ top:15%; left:75%; animation-delay:-2s; }

  @keyframes float {
    0%{transform:translateY(0) rotate(0)}
    25%{transform:translateY(-20px) rotate(90deg)}
    50%{transform:translateY(-40px) rotate(180deg)}
    75%{transform:translateY(-20px) rotate(270deg)}
    100%{transform:translateY(0) rotate(360deg)}
  }

  img { width:100%; height:100%; object-fit:contain; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.1)); }
  @media (max-width: 480px){ .robot-icon { width:60px; height:60px; } }
`;

const Card = styled.div`
  position: relative; z-index: 5; width: 100%; max-width: 440px;
  background: rgba(255,255,255,0.95); backdrop-filter: blur(20px);
  border-radius: 24px; padding: 48px;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1);
  animation: slideUp 0.8s ease-out;

  @keyframes slideUp {
    from { opacity:0; transform: translateY(30px) scale(0.95); }
    to { opacity:1; transform: translateY(0) scale(1); }
  }

  @media (max-width: 480px){ margin: 20px; padding: 32px 24px; max-width: none; }
`;

const LogoSection = styled.div`
  text-align: center; margin-bottom: 40px;

  .logo-container { display:inline-flex; align-items:center; justify-content:center; margin-bottom:16px; }
  .logo { width:64px; height:64px; margin-right:12px; animation: bounce 2s infinite; }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0) }
    40% { transform: translateY(-8px) }
    60% { transform: translateY(-4px) }
  }

  .brand-text { font-size:32px; font-weight:800; color:#1f2937; letter-spacing:-0.02em; }
  .brand-dots { color:#f97316; }
  .tagline { color:#6b7280; font-size:14px; font-weight:500; text-transform:uppercase; letter-spacing:0.5px; margin-top:8px; }
`;
const ProviderIcon = styled.img`
  width: 18px;
  height: 18px;
  display: block;
`;

const H1 = styled.h1` font-size:28px; font-weight:700; color:#1f2937; margin-bottom:8px; text-align:center; `;
const Subtitle = styled.p` color:#6b7280; font-size:16px; text-align:center; margin-bottom:24px; line-height:1.5; `;

const FormGroup = styled.div` margin-bottom:24px; `;
const Label = styled.label` display:block; color:#374151; font-weight:600; font-size:14px; margin-bottom:8px; `;
const Input = styled.input`
  width:100%; padding:16px 20px; border:2px solid #e5e7eb; border-radius:16px; font-size:16px; background:rgba(255,255,255,0.9);
  transition: all .3s ease;
  &:focus{ outline:none; border-color:#f97316; box-shadow:0 0 0 4px rgba(249,115,22,0.1), 0 4px 20px rgba(0,0,0,0.1); background:#fff; }
  &::placeholder{ color:#9ca3af; }
`;

const Row = styled.div` display:flex; align-items:center; gap:12px; margin:16px 0; `;
const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width:20px; height:20px; accent-color:#f97316; cursor:pointer;
`;
const Small = styled.span` color:#6b7280; font-size:14px; `;
const PrimaryBtn = styled.button`
  width:100%; background:linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  border:none; border-radius:16px; color:#fff; padding:18px 24px; font-size:16px; font-weight:700; cursor:pointer; transition:all .3s ease;
  box-shadow: 0 8px 20px rgba(249,115,22,0.3), 0 2px 8px rgba(0,0,0,0.1);
  position:relative; overflow:hidden; margin-top: 4px;
  &:before{ content:''; position:absolute; top:0; left:-100%; width:100%; height:100%;
    background:linear-gradient(90deg, transparent, rgba(255,255,255,.2), transparent); transition:left .5s ease; }
  &:hover:before{ left:100%; }
  &:hover{ transform: translateY(-2px); box-shadow: 0 12px 30px rgba(249,115,22,.4), 0 4px 15px rgba(0,0,0,.15); }
  &:active{ transform: translateY(0); }
`;
const MutedBtn = styled(PrimaryBtn)` background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); `;
const Footer = styled.div` text-align:center; margin-top:32px; color:#6b7280; font-size:14px; `;
const Tiny = styled.div` margin-top:12px; font-size:12px; color:#374151; opacity:0.9; word-break: break-all; `;

const MFAInfo = styled.div`
  display: flex; align-items: center; gap: 16px;
  background: rgba(249, 115, 22, 0.1);
  padding: 20px; border-radius: 16px; margin-bottom: 24px;
`;

const MFAIcon = styled.div`
  width: 48px; height: 48px; background: #f97316; border-radius: 12px;
  display:flex; align-items:center; justify-content:center;
  color:#fff; font-size:24px; flex-shrink:0;
`;

const MFAText = styled.div`
  color:#374151; font-size:14px; line-height:1.5;
`;

const H3 = styled.h3`
  font-size:16px; font-weight:700; color:#1f2937; margin: 12px 0 8px;
`;

const SecretRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
`;

const SecretBox = styled.div`
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-user-select: all;
  user-select: all;
`;

const CopyBtn = styled.button`
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s ease;
  &:hover { background:#fff; box-shadow:0 4px 14px rgba(0,0,0,.08); }
  &:active { transform: translateY(1px); }
`;

const QRWrap = styled.div`
  display: grid;
  place-items: center;
  margin: 12px 0 2px;
  padding: 16px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
`;

const SuccessBox = styled.div`
  color:#065f46;
  background:rgba(16,185,129,0.12);
  border:1px solid rgba(16,185,129,0.28);
  padding:12px 14px; border-radius:12px; margin:12px 0;
  font-size:14px; font-weight:600;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
  &:hover { text-decoration: none; }
`;

function canUseLocalStorage() {
  try {
    const k = '__ls__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

async function parseJsonBestEffort(resp){
  const ct = resp.headers.get('content-type') || '';
  const raw = await resp.clone().text();
  try { return ct.includes('application/json') && raw ? JSON.parse(raw) : raw || null; }
  catch { return raw || null; }
}

function microsoftSignInErrorMessage(msError, msMessage) {
  const code = String(msError || '').toLowerCase();
  const detail = String(msMessage || '').trim();
  const detailLower = detail.toLowerCase();

  if (code === 'access_denied' || detailLower.includes('aadsts65004')) {
    return (
      'Microsoft sign-in was not completed. If your IT admin just approved access, '
      + 'close old Microsoft tabs and try again from app.codiii.com '
      + '(do not use Back to App on an old approval screen).'
    );
  }
  if (code === 'state_mismatch') {
    return 'Your sign-in session expired. Please click Continue with Microsoft again.';
  }
  if (code === 'missing_code') {
    return 'Microsoft sign-in did not finish. Please try again.';
  }
  if (detail) {
    const short = detail.length > 240 ? `${detail.slice(0, 240)}…` : detail;
    return `Microsoft sign-in failed: ${short}`;
  }
  if (code) {
    return `Microsoft sign-in failed (${msError}). Please try again.`;
  }
  return 'Microsoft sign-in failed. Please try again.';
}

async function msftFinalizeIfNeeded({ API_BASE, id_token, state }) {
  if (!state) return { skipped: true, reason: "no_state" };
  if (state.startsWith('desktop:')) return { skipped: true, reason: "desktop_state" };

  try {
    const resp = await fetch(`${API_BASE}/api/auth/msft/finalize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${id_token}`,
      },
      body: JSON.stringify({ state }),
    });

    const data = await parseJsonBestEffort(resp);
    if (!resp.ok) {
      console.error("[msftFinalize] failed", resp.status, data);
      return { ok: false, status: resp.status, data };
    }
    console.log("[msftFinalize] success", data);
    return { ok: true, data };
  } catch (e) {
    console.error("[msftFinalize] exception", e);
    return { ok: false, error: String(e) };
  }
}

async function googleFinalizeIfNeeded({ API_BASE, id_token, google_state }) {
  if (!google_state) return { skipped: true, reason: "no_google_state" };

  try {
    const resp = await fetch(`${API_BASE}/api/auth/google/finalize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${id_token}`,
      },
      body: JSON.stringify({ state: google_state }),
    });

    const data = await parseJsonBestEffort(resp);
    if (!resp.ok) {
      console.error("[googleFinalize] failed", resp.status, data);
      return { ok: false, status: resp.status, data };
    }
    console.log("[googleFinalize] success", data);
    return { ok: true, data };
  } catch (e) {
    console.error("[googleFinalize] exception", e);
    return { ok: false, error: String(e) };
  }
}

export default function SignIn(){
  const nav = useNavigate();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const rawState = qs.get('state') || '';
  const googleState = qs.get('google_state') || '';
  const isDesktopState = rawState.startsWith('desktop:');

  useEffect(() => {
    console.group('[SignIn] mount');
    console.log('location.href  :', window.location.href);
    console.log('location.search:', search);
    console.log('location.hash  :', window.location.hash);
    console.log('rawState       :', rawState);
    console.log('isDesktopState :', isDesktopState);
    console.groupEnd();
  }, [search, rawState, isDesktopState]);

  function isDesktopMode() {
    return !!rawState && rawState.startsWith('desktop:');
  }

  async function completeLogin(tokens, source = 'unknown') {
    console.log('[SignIn] completeLogin', {
      source,
      isDesktopMode: isDesktopMode(),
      rawState,
    });

    if (isDesktopMode()) {
      return finishDesktopFlow(tokens, rawState);
    }

    return saveTokensAndGo(tokens, source);
  }

  const [step, setStep] = useState('email');

  const [email, setEmail] = useState(qs.get('email') || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(search);
    const msError = params.get('ms_error');
    const msMessage = params.get('ms_message');
    if (!msError && !msMessage) return;

    setError(microsoftSignInErrorMessage(msError, msMessage));

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('ms_error');
      url.searchParams.delete('ms_message');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, [search]);

  const [challenge, setChallenge] = useState(null);
  const [session, setSession] = useState(null);

  const [totpSecret, setTotpSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  const [copied, setCopied] = useState(false);

  const [fpSending, setFpSending]   = useState(false);
  const [fpSent, setFpSent]         = useState(false);
  const [fpCooldown, setFpCooldown] = useState(0);
  const [resetCode, setResetCode]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [newPw2, setNewPw2]         = useState('');
  const [showNewPw, setShowNewPw]   = useState(false);

  const [pwChanged, setPwChanged]   = useState(false);

  const [newUserPw, setNewUserPw] = useState('');
  const [newUserPw2, setNewUserPw2] = useState('');
  const [requiredAttrs, setRequiredAttrs] = useState([]);
  const [attrValues, setAttrValues] = useState({});  

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.group('[SignIn] mounted');
      console.log('API_BASE configured:', !!API_BASE, 'localStorage:', canUseLocalStorage());
      console.groupEnd();
    }
  }, [API_BASE]);

  useEffect(() => {
    const h = window.location.hash?.replace(/^#/, '') || '';
    if (!h) {
      console.log('[SignIn][hash] No hash present, skipping hosted UI callback handling');
      return;
    }

    const params = new URLSearchParams(h);
    const idt = params.get('id_token');
    const at  = params.get('access_token');
    const rt  = params.get('refresh_token');
    const stateFromHash = params.get('state');  

    console.group('[SignIn][hash callback]');
    console.log('raw hash                :', h);
    console.log('id_token present?       :', !!idt);
    console.log('access_token present?   :', !!at);
    console.log('refresh_token present?  :', !!rt);
    console.log('state from hash         :', stateFromHash);
    console.log('is desktop state (hash) :', !!(stateFromHash && stateFromHash.startsWith('desktop:')));
    console.groupEnd();

    if (!(at || idt)) {
      console.log('[SignIn][hash] Hash has no tokens, ignoring');
      return;
    }

    const tokens = { id_token: idt, access_token: at, refresh_token: rt };

    (async () => {
      if (stateFromHash && stateFromHash.startsWith('desktop:')) {
        console.log('[SignIn][hash] Calling finishDesktopFlow from hash callback');
        finishDesktopFlow(tokens, stateFromHash);
        return;
      }

      await msftFinalizeIfNeeded({
        API_BASE,
        id_token: idt,
        state: stateFromHash,
      });

      // If google_state is present in the URL query params, finalize Google Drive connection
      const qsParams = new URLSearchParams(window.location.search);
      const stateParam = stateFromHash || '';
      if (stateParam.startsWith('google:')) {
        const pendingGoogleState = stateParam.replace('google:', '');
        await googleFinalizeIfNeeded({
          API_BASE,
          id_token: idt,
          google_state: pendingGoogleState,
        });
      }

      console.log('[SignIn][hash] Calling saveTokensAndGo (web login only)');
      saveTokensAndGo(tokens, 'hash-callback-web');
    })();
  }, []);

  useEffect(() => {
    const h = window.location.hash?.replace(/^#/, '') || '';
    if (!h) return;

    const p = new URLSearchParams(h);
    const status = p.get('status');
    const reason = p.get('reason');
    const connect = p.get('connect'); 

    if (status !== 'cancelled' && status !== 'error') return;

    const provider =
      connect === 'google' ? 'Google' :
      connect === 'microsoft' ? 'Microsoft' :
      'Sign-in';

    if (status === 'cancelled') {
      setError(`${provider} sign-in was cancelled. Please try again.`);
    } else if (reason === 'state_mismatch') {
      setError('Your sign-in session expired. Please try again.');
    } else {
      setError(`${provider} sign-in didn't finish. Please try again.`);
    }

    const url = new URL(window.location.href);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }, []);

  useEffect(() => {
    if (!googleState) return;

    const storedIdToken = localStorage.getItem('id_token');
    if (!storedIdToken) {
      console.log('[SignIn][googleState] No id_token in localStorage, waiting for hash callback to handle it');
      return;
    }

    console.log('[SignIn][googleState] Found google_state and existing id_token, finalizing Google Drive');
    (async () => {
      await googleFinalizeIfNeeded({
        API_BASE,
        id_token: storedIdToken,
        google_state: googleState,
      });
      window.location.replace('/');
    })();
  }, [googleState]);

  function saveTokensAndGo(tokens, source = 'unknown') {
    console.group('[SignIn] saveTokensAndGo');
    console.log('called from        :', source);
    console.log('tokens keys        :', Object.keys(tokens || {}));
    console.log('id_token present?  :', !!tokens?.id_token);
    console.log('access_token?      :', !!tokens?.access_token);
    console.log('refresh_token?     :', !!tokens?.refresh_token);

    if (tokens?.access_token) localStorage.setItem('access_token', tokens.access_token);
    if (tokens?.id_token)     localStorage.setItem('id_token', tokens.id_token);
    if (tokens?.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token);

    const wrote = {
      id_token: !!localStorage.getItem('id_token'),
      access_token: !!localStorage.getItem('access_token'),
      refresh_token: !!localStorage.getItem('refresh_token'),
    };
    console.log('wrote to localStorage:', wrote);
    console.groupEnd();

    if (!wrote.access_token && !wrote.id_token) {
      setError('No tokens returned; sign-in incomplete.');
      return;
    }
    window.dispatchEvent(new Event('auth-tokens-changed'));
    console.log('[SignIn] Navigating to "/" after web login');
    window.location.replace('/');
  }

  async function finishDesktopFlow(tokens, desktopState) {
    console.group('[SignIn] finishDesktopFlow');
    console.log('desktopState param     :', desktopState);
    console.log('tokens present         :', {
      id_token: !!tokens?.id_token,
      access_token: !!tokens?.access_token,
      refresh_token: !!tokens?.refresh_token,
    });

    try {
      const resp = await fetch(`${API_BASE}/api/auth/desktop/issue-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_token: tokens.id_token,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          expires_in: tokens.expires_in,
          token_type: tokens.token_type || 'Bearer',
        }),
      });

      const data = await parseJsonBestEffort(resp);
      console.log('[SignIn] /desktop/issue-code status:', resp.status);
      console.log('[SignIn] /desktop/issue-code body  :', data);

      if (!resp.ok) {
        setError(
          typeof data === 'string'
            ? data
            : data?.detail || 'Desktop linking failed. You are signed in on the web only.'
        );
        console.warn('[SignIn] issue-code failed, falling back to web login');
        saveTokensAndGo(tokens, 'desktop-fallback-issue-code-failed');
        console.groupEnd();
        return;
      }

      const code = data?.code;
      console.log('desktop code from backend:', code);

      if (!code) {
        setError('Desktop linking failed: missing code from server.');
        console.warn('[SignIn] No code returned, falling back to web login');
        saveTokensAndGo(tokens, 'desktop-fallback-no-code');
        console.groupEnd();
        return;
      }

      if (tokens?.access_token) localStorage.setItem('access_token', tokens.access_token);
      if (tokens?.id_token)     localStorage.setItem('id_token', tokens.id_token);
      if (tokens?.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token);

      const url = `codiii-desktop://callback?code=${encodeURIComponent(
        code
      )}&state=${encodeURIComponent(desktopState)}`;

      console.log('[SignIn] Redirecting back to desktop via URL:', url);
      console.groupEnd();
      window.location.assign(url);
    } catch (err) {
      console.error('[SignIn] finishDesktopFlow error:', err);
      setError(err?.message || String(err));
      console.warn('[SignIn] Error, falling back to web login');
      saveTokensAndGo(tokens, 'desktop-fallback-error');
      console.groupEnd();
    }
  }

  function handleMicrosoftSSO() {
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    window.location.assign(`${API_BASE}/msft/connect-start`);
  }
  function handleGoogleSSO() {
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    window.location.assign(`${API_BASE}/google/connect-start`);
  }

  function startFpCooldown(sec = 30){
    setFpCooldown(sec);
    const id = setInterval(() => {
      setFpCooldown(s => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  async function onForgotStart(e){
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email.'); return; }
    if (fpSending) return;

    setFpSending(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email })
      });
      const data = await parseJsonBestEffort(r);
      if (!r.ok) throw new Error(typeof data==='string' ? data : (data?.detail || 'Could not send reset code'));

      setFpSent(true);
      setStep('forgotConfirm');
      startFpCooldown(30);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setFpSending(false);
    }
  }

  async function onForgotResend(e){
    e.preventDefault();
    if (fpCooldown > 0 || fpSending) return;
    setError('');
    setFpSending(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email })
      });
      const data = await parseJsonBestEffort(r);
      if (!r.ok) throw new Error(typeof data==='string' ? data : (data?.detail || 'Could not resend code'));

      setFpSent(true);
      startFpCooldown(30);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setFpSending(false);
    }
  }

  async function onSubmitNewPassword(e){
    e.preventDefault();
    setError('');

    if (!session) { setError('Missing session'); return; }
    if (!newUserPw || !newUserPw2) { setError('Enter and confirm your new password.'); return; }
    if (newUserPw !== newUserPw2) { setError('Passwords do not match.'); return; }

    for (const k of requiredAttrs) {
      if (!attrValues[k] || String(attrValues[k]).trim() === '') {
        setError(`Please fill ${k}.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/respond-challenge`, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          email,
          session,
          challenge: 'NEW_PASSWORD_REQUIRED',
          new_password: newUserPw,
          user_attributes: attrValues, 
        })
      });
      const out = await parseJsonBestEffort(resp);
      if (!resp.ok) {
        setError(typeof out === 'string' ? out : (out?.detail || `New password failed (${resp.status})`));
        return;
      }

      if (out?.challenge === 'SOFTWARE_TOKEN_MFA') {
        setChallenge('SOFTWARE_TOKEN_MFA'); setSession(out.session); setStep('softwareMfa'); return;
      }
      if (out?.challenge === 'MFA_SETUP') {
        setChallenge('MFA_SETUP'); setSession(out.session); setStep('mfaSetup'); return;
      }
      if (out?.challenge === 'SELECT_CHALLENGE') {
        setChallenge('SELECT_CHALLENGE'); setSession(out.session); setStep('select'); return;
      }

      saveTokensAndGo(out, 'email-otp-success-1');
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onForgotConfirm(e){
    e.preventDefault();
    setError('');
    if (!email || !resetCode || !newPw) { setError('Fill all fields.'); return; }
    if (newPw !== newPw2) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/forgot-password/confirm`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, code: resetCode, new_password: newPw })
      });
      const data = await parseJsonBestEffort(r);
      if (!r.ok) throw new Error(typeof data==='string' ? data : (data?.detail || 'Reset failed'));

      setPwChanged(true);
      setStep('password');
      setResetCode(''); setNewPw(''); setNewPw2('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function onEmailNext(e) {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email.'); return; }
    setStep('password');
  }
  
  function handleCopySecret(secret) {
    navigator.clipboard?.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function onSignIn(e){
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter your email and password.'); return; }

    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        mode: 'cors',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ email, password })
      });
      const data = await parseJsonBestEffort(resp);

      if (!resp.ok) {
        const msg = typeof data === 'string' ? data : (data?.detail || `Sign in failed (${resp.status})`);
        if (String(msg).toLowerCase().includes('not confirmed')) {
          nav('/auth/confirm', { state: { email } });
          return;
        }
        setError(msg);
        return;
      }

      if (data?.challenge === 'SELECT_CHALLENGE') {
        setChallenge('SELECT_CHALLENGE'); setSession(data.session); setStep('select'); return;
      }
      if (data?.challenge === 'MFA_SETUP') {
        setChallenge('MFA_SETUP'); setSession(data.session); setStep('mfaSetup'); return;
      }
      if (data?.challenge === 'SOFTWARE_TOKEN_MFA') {
        setChallenge('SOFTWARE_TOKEN_MFA'); setSession(data.session); setStep('softwareMfa'); return;
      }
      if (data?.challenge === 'NEW_PASSWORD_REQUIRED') {
        const raw = data?.parameters?.requiredAttributes;
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (typeof raw === 'string') {
          try { list = JSON.parse(raw); } catch { list = raw.split(',').map(s=>s.trim()); }
        }
        
        const clean = list
          .map(s => s.replace(/^userAttributes\./, ''))
          .filter(Boolean);

        setRequiredAttrs(clean);                
        setAttrValues((prev)=> {
          const next = { ...prev };
          clean.forEach(k => { if (!(k in next)) next[k] = ''; });
          return next;
        });

        setChallenge('NEW_PASSWORD_REQUIRED');
        setSession(data.session);
        setStep('newPassword');
        return;
      }

      await completeLogin(data, 'password-login');
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function chooseEmailOtp() {
    setError('');
    if (!session) { setError('Missing session'); return; }

    const resp = await fetch(`${API_BASE}/api/auth/select-challenge`, {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ email, session, challenge: 'EMAIL_OTP' })
    });
    const out = await parseJsonBestEffort(resp);
    if (!resp.ok) { setError(out?.detail || 'Failed to start Email OTP'); return; }
    setChallenge(out.challenge || 'EMAIL_OTP');
    setSession(out.session || session);
    setStep('emailOtp');
  }

  async function chooseAuthenticator() {
    setError('');
    if (!session) { setError('Missing session'); return; }

    if (challenge === 'SELECT_CHALLENGE') {
      const resp = await fetch(`${API_BASE}/api/auth/select-challenge`, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ email, session, challenge: 'SOFTWARE_TOKEN_MFA' })
      });
      const out = await parseJsonBestEffort(resp);
      if (!resp.ok) { setError(out?.detail || 'Failed to start authenticator flow'); return; }
      setChallenge(out.challenge);
      setSession(out.session);
      if (out.challenge === 'MFA_SETUP') setStep('mfaSetup');
      if (out.challenge === 'SOFTWARE_TOKEN_MFA') setStep('softwareMfa');
    }

    if (challenge === 'MFA_SETUP') {
      const a = await fetch(`${API_BASE}/api/auth/mfa/associate`, {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ email, session, issuer: 'CODiii' })
      });
      const assoc = await parseJsonBestEffort(a);
      if (!a.ok) { setError(typeof assoc === 'string' ? assoc : assoc?.detail || 'MFA associate failed'); return; }
      setSession(assoc.session || session);
      setTotpSecret(assoc.secret_code || '');
      setOtpauthUrl(assoc.otpauth_url || '');
      setStep('mfaSetup');
    }
  }

  async function submitEmailOtp(code) {
    setError('');
    if (!code) { setError('Code'); return; }
    if (!session) { setError('Missing session'); return; }

    const resp = await fetch(`${API_BASE}/api/auth/respond-challenge`, {
      method: 'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ email, challenge: 'EMAIL_OTP', session, code })
    });
    const out = await parseJsonBestEffort(resp);
    if (!resp.ok) { setError(out?.detail || 'Invalid code'); return; }
    saveTokensAndGo(out, 'email-otp-success-2');
  }

  async function onSubmitTotpSetup(e){
    e.preventDefault();
    setError('');
    if (!totpCode || !session) { setError('Enter the 6-digit code.'); return; }
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/mfa/verify-complete`, {
        method: 'POST',
        mode: 'cors',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ email, session, code: totpCode, device_name: 'Authenticator' })
      });
      const data = await parseJsonBestEffort(resp);
      if (!resp.ok) { setError(typeof data === 'string' ? data : (data?.detail || `MFA verify failed (${resp.status})`)); return; }

      if (data?.challenge === 'SOFTWARE_TOKEN_MFA') {
        setChallenge('SOFTWARE_TOKEN_MFA');
        setSession(data.session);
        setTotpCode('');
        setStep('softwareMfa');
        return;
      }
      await completeLogin(data, 'totp-setup-verify-complete');
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitSoftwareMfa(e){
    e.preventDefault();
    setError('');
    if (!totpCode || !session) { setError('Enter the 6-digit code.'); return; }
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/auth/respond-challenge`, {
        method: 'POST',
        mode: 'cors',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ email, challenge: 'SOFTWARE_TOKEN_MFA', session, code: totpCode })
      });
      const data = await parseJsonBestEffort(resp);
      if (!resp.ok) { setError(typeof data === 'string' ? data : (data?.detail || `MFA code failed (${resp.status})`)); return; }
      await completeLogin(data, 'totp-setup-complete');

    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const ErrorBox = error ? (
    <div style={{
      color:'#b91c1c', background:'rgba(185,28,28,0.08)',
      border:'1px solid rgba(185,28,28,0.25)', padding:'12px 14px',
      borderRadius:12, marginBottom:16
    }}>
      {error}
    </div>
  ) : null;

  const ATTR_META = {
    name:         { label: "Enter your name",  placeholder: "Name" },
    given_name:   { label: "First name",       placeholder: "Jane" },
    family_name:  { label: "Last name",        placeholder: "Doe" },
    phone_number: { label: "Phone number",     placeholder: "+1 555 123 4567", inputMode: "tel" },
  };

  function labelFor(attr) {
    const m = ATTR_META[attr];
    if (m?.label) return m.label;
    return attr.replace(/^custom:/,'').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  function placeholderFor(attr) {
    const m = ATTR_META[attr];
    if (m?.placeholder) return m.placeholder;
    return `Enter ${labelFor(attr).toLowerCase()}`;
  }

  function inputPropsFor(attr) {
    const m = ATTR_META[attr] || {};
    const props = {};
    if (m.inputMode) props.inputMode = m.inputMode;
    return props;
  }

  return (
    <PageWrap>
      <RobotPattern>
        {[1,2,3,4,5,6,7,8].map((i) => (
          <div key={i} className="robot-icon">
            <img src={i <= 5 ? robotColor : robotWhite} alt="CODiii Robot" />
          </div>
        ))}
      </RobotPattern>

      <Card>
        <LogoSection>
          <div className="logo-container">
            <div className="logo"><img src={mascot} alt="CODiii Logo" /></div>
            <span className="brand-text">COD<span className="brand-dots">iii</span></span>
          </div>
          <div className="tagline">The Design & Construction Brain</div>
        </LogoSection>

        {step === 'email' && (
          <>
            <H1>Sign in</H1>
            <Subtitle>Sign in to CODiii with your work account.</Subtitle>

            {ErrorBox}

            <PrimaryBtn
              type="button"
              onClick={handleMicrosoftSSO}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <ProviderIcon src={msLogo} alt="" aria-hidden="true" />
              Continue with Microsoft
            </PrimaryBtn>

            <button
              type="button"
              onClick={handleGoogleSSO}
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0,
                width: "100%",
                height: 48,
                backgroundColor: "#fff",
                border: "1px solid #dadce0",
                borderRadius: 4,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                fontSize: 16,
                fontWeight: 500,
                color: "#757575",
                letterSpacing: "0.02em",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                padding: 0,
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRight: "1px solid #dadce0",
                flexShrink: 0,
              }}>
                <svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span style={{ flex: 1, textAlign: "center" }}>Sign in with Google</span>
            </button>
          </>
        )}

        {step === 'password' && (
          <>
            <H1>Enter your password</H1>
            <form onSubmit={onSignIn}>
              <FormGroup>
                <Label htmlFor="signin-password">Password</Label>
                <Input
                  id="signin-password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </FormGroup>
              <Row>
                <Checkbox id="show-password" checked={showPw} onChange={e=>setShowPw(e.target.checked)} />
                <Small><label htmlFor="show-password">Show password</label></Small>
                <div style={{ marginLeft:'auto' }}>
                  <LinkBtn type="button" onClick={() => setStep('forgotStart')}>
                    Forgot your password?
                  </LinkBtn>
                </div>
              </Row>
              {pwChanged && <SuccessBox>Password updated. Please sign in.</SuccessBox>}
              {ErrorBox}
              <PrimaryBtn type="submit" disabled={submitting}>{submitting ? 'Signing in…' : 'Continue'}</PrimaryBtn>
              <MutedBtn type="button" onClick={()=>setStep('email')} style={{marginTop:8}}>Back</MutedBtn>
            </form>
          </>
        )}

        {step === 'select' && (
          <>
            <H1>Choose your second factor</H1>
            <Subtitle>Pick how you want to verify.</Subtitle>

            <MFAInfo>
              <MFAIcon>🔐</MFAIcon>
              <MFAText>
                You can receive a one-time code via email, or use an authenticator app for stronger security.
              </MFAText>
            </MFAInfo>

            {ErrorBox}
            <div style={{display:'grid', gap:12, margin:'12px 0'}}>
              {/* If you want email OTP back, hook this up to chooseEmailOtp */}
              {/* <PrimaryBtn type="button" onClick={chooseEmailOtp}>Send code to my email</PrimaryBtn> */}
              <PrimaryBtn type="button" onClick={chooseAuthenticator}>Use authenticator app</PrimaryBtn>
            </div>

            <MutedBtn type="button" onClick={()=>setStep('password')} style={{marginTop:8}}>Back</MutedBtn>
          </>
        )}

        {step === 'emailOtp' && (
          <>
            <H1>Email verification</H1>
            <Subtitle>Enter the 6-digit code we sent to your email.</Subtitle>
            <form onSubmit={(e)=>{ e.preventDefault(); submitEmailOtp(e.currentTarget.elements.code.value); }}>
              <FormGroup>
                <Label htmlFor="email-otp">Code</Label>
                <Input id="email-otp" name="code" inputMode="numeric" pattern="\d*" maxLength={6} required />
              </FormGroup>
              {ErrorBox}
              <PrimaryBtn type="submit" disabled={submitting}>{submitting ? 'Verifying…' : 'Verify'}</PrimaryBtn>
              <MutedBtn type="button" onClick={()=>setStep('select')} style={{marginTop:8}}>Back</MutedBtn>
            </form>
          </>
        )}

        {step === 'mfaSetup' && (
          <>
            <H1>Authenticator app MFA</H1>

            {!totpSecret ? (
              <>
                <MFAInfo>
                  <MFAIcon>📱</MFAIcon>
                  <MFAText>
                    Scan the QR in your authenticator app (Google Authenticator, Authy, 1Password)
                    or choose email code.
                  </MFAText>
                </MFAInfo>

                {ErrorBox}
                <div style={{display:'grid', gap:12}}>
                  <PrimaryBtn type="button" onClick={chooseAuthenticator}>Use authenticator app</PrimaryBtn>
                  {/* <PrimaryBtn type="button" onClick={chooseEmailOtp}>Send code to my email</PrimaryBtn> */}
                </div>

                <MutedBtn type="button" onClick={()=>setStep('password')} style={{marginTop:8}}>Back</MutedBtn>
              </>
            ) : (
              <>
                <MFAInfo>
                  <MFAIcon>📱</MFAIcon>
                  <MFAText>
                    Scan the QR with your authenticator app, then enter the 6-digit code.
                  </MFAText>
                </MFAInfo>

                <FormGroup>
                  <Label>Secret</Label>
                  <SecretRow>
                    <SecretBox title={totpSecret}>{totpSecret}</SecretBox>
                    <CopyBtn type="button" onClick={() => handleCopySecret(totpSecret)}>
                      {copied ? "✓ Copied" : "Copy"}
                    </CopyBtn>
                  </SecretRow>
                </FormGroup>

                <QRWrap>
                  <QRCodeCanvas value={otpauthUrl || ''} size={192} includeMargin />
                </QRWrap>

                <form onSubmit={onSubmitTotpSetup}>
                  <FormGroup>
                    <Label htmlFor="totp-code">6-digit code</Label>
                    <Input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={6}
                      value={totpCode}
                      onChange={e=>setTotpCode(e.target.value)}
                      placeholder="Enter code"
                      required
                    />
                  </FormGroup>
                  {ErrorBox}
                  <PrimaryBtn type="submit" disabled={submitting}>
                    {submitting ? 'Verifying…' : 'Verify & Continue'}
                  </PrimaryBtn>
                  <div style={{marginTop:12}}>
                    {/* <LinkBtn type="button" onClick={chooseEmailOtp}>Use email code instead</LinkBtn> */}
                  </div>
                </form>
              </>
            )}
          </>
        )}

        {step === 'softwareMfa' && (
          <>
            <H1>Verify code</H1>

            <MFAInfo>
              <MFAIcon>📱</MFAIcon>
              <MFAText>Enter the 6-digit code from your authenticator app.</MFAText>
            </MFAInfo>

            <form onSubmit={onSubmitSoftwareMfa}>
              <FormGroup>
                <Label htmlFor="totp-code-2">6-digit code</Label>
                <Input
                  id="totp-code-2"
                  type="text"
                  value={totpCode}
                  onChange={e=>setTotpCode(e.target.value)}
                  placeholder="Enter code"
                  required
                />
              </FormGroup>
              {ErrorBox}
              <PrimaryBtn type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Continue'}
              </PrimaryBtn>
              <MutedBtn type="button" onClick={()=>setStep('select')} style={{marginTop:8}}>Back</MutedBtn>
            </form>
          </>
        )}

        {step === 'forgotStart' && (
          <>
            <H1>Reset Password</H1>
            <Subtitle>Enter your email and we’ll send a reset code.</Subtitle>
            <form onSubmit={onForgotStart}>
              <FormGroup>
                <Label htmlFor="forgot-email">Email address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="name@host.com"
                  value={email}
                  onChange={e=>{ setEmail(e.target.value); setFpSent(false); }}
                  required
                />
              </FormGroup>
              {ErrorBox}
              <PrimaryBtn type="submit" disabled={fpSending}>
                {fpSending ? 'Sending…' : 'Send Reset Code'}
              </PrimaryBtn>
              <MutedBtn type="button" onClick={()=>setStep('password')} style={{marginTop:8}}>
                Back to Sign In
              </MutedBtn>
            </form>
          </>
        )}

        {step === 'forgotConfirm' && (
          <>
            <H1>Check your email</H1>
            <Subtitle>
              We sent a 6-digit code to <b>{email || 'your email'}</b>. Enter it below and choose a new password.
            </Subtitle>

            {fpSent && (
              <SuccessBox>
                Code sent! {fpCooldown > 0 ? `You can resend in ${fpCooldown}s.` : ''}
              </SuccessBox>
            )}

            <form onSubmit={onForgotConfirm}>
              <FormGroup>
                <Label htmlFor="reset-code">6-digit code</Label>
                <Input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={6}
                  value={resetCode}
                  onChange={e=>setResetCode(e.target.value)}
                  placeholder="Enter code"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type={showNewPw ? 'text':'password'}
                  value={newPw}
                  onChange={e=>setNewPw(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="new-password-2">Confirm new password</Label>
                <Input
                  id="new-password-2"
                  type={showNewPw ? 'text':'password'}
                  value={newPw2}
                  onChange={e=>setNewPw2(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </FormGroup>

              <Row>
                <Checkbox id="show-new-password" checked={showNewPw} onChange={e=>setShowNewPw(e.target.checked)} />
                <Small><label htmlFor="show-new-password">Show password</label></Small>
                <div style={{marginLeft:'auto'}}>
                  Didn’t get a code?{' '}
                  <LinkBtn
                    type="button"
                    onClick={onForgotResend}
                    disabled={fpSending || fpCooldown > 0}
                    style={{ opacity:(fpSending || fpCooldown>0)?0.6:1, cursor:(fpSending || fpCooldown>0)?'not-allowed':'pointer' }}
                  >
                    {fpSending ? 'Sending…' : (fpCooldown>0 ? `Resend in ${fpCooldown}s` : 'Resend')}
                  </LinkBtn>
                </div>
              </Row>

              {ErrorBox}

              <PrimaryBtn type="submit" disabled={submitting}>
                {submitting ? 'Updating…' : 'Set New Password'}
              </PrimaryBtn>

              <MutedBtn type="button" onClick={()=>setStep('password')} style={{marginTop:8}}>
                Back
              </MutedBtn>
            </form>
          </>
        )}

        {step === 'newPassword' && (
          <>
            <H1>Set a new password</H1>
            <Subtitle>It’s your first sign-in. Please fill the required details.</Subtitle>

            <form onSubmit={onSubmitNewPassword}>
              <FormGroup>
                <Label htmlFor="npw1">New password</Label>
                <Input
                  id="npw1"
                  type="password"
                  value={newUserPw}
                  onChange={e=>setNewUserPw(e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="npw2">Confirm new password</Label>
                <Input
                  id="npw2"
                  type="password"
                  value={newUserPw2}
                  onChange={e=>setNewUserPw2(e.target.value)}
                  required
                />
              </FormGroup>

              {requiredAttrs.map((attr) => (
                <FormGroup key={attr}>
                  <Label htmlFor={`attr-${attr}`}>{labelFor(attr)}</Label>
                  <Input
                    id={`attr-${attr}`}
                    type="text"
                    {...inputPropsFor(attr)}
                    value={attrValues[attr] ?? ''}
                    onChange={e => setAttrValues(v => ({ ...v, [attr]: e.target.value }))}
                    placeholder={placeholderFor(attr)}
                    required
                  />
                </FormGroup>
              ))}

              {ErrorBox}
              <PrimaryBtn type="submit" disabled={submitting}>
                {submitting ? 'Updating…' : 'Set Password & Continue'}
              </PrimaryBtn>
              <MutedBtn type="button" onClick={()=>setStep('password')} style={{marginTop:8}}>Back</MutedBtn>
            </form>
          </>
        )}
      </Card>
    </PageWrap>
  );
}
